import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import { mcKeys } from '../../core/query-keys';
import { mcEndpoints } from './microcontrollers.endpoints';
import type {
  Microcontroller,
  McStatusEntry,
  McHistoryEntry,
  McChild,
  CreateMcRequest,
  UpdateMcRequest,
  RetireMcRequest,
  McFilter,
  McStatusFilter,
  AssignGrowRoomRequest,
  AssignGrowRoomRackRequest,
  AssignGrowRoomRackLevelRequest,
  AssignLocationRequest,
  AssignPlumbingRoomRequest,
  AssignPlumbingNodeRequest,
} from './microcontrollers.types';

// List endpoints return a paginated envelope `{ <field>: [...], next_page_token }` inside the
// `data` wrapper (which `api.get` already unwraps).
type Paged<K extends string, T> = { [P in K]: T[] } & { next_page_token?: string };

/** The largest page iot-manager serves (`parse_page`: `size.min(500)`). */
const MC_PAGE_SIZE = 500;
/** A backstop, not a limit anyone should reach: 40 × 500 boards. A token that never
 *  empties would otherwise spin forever. */
const MAX_PAGES = 40;

/** Follows `next_page_token` until the server sends an empty one.
 *
 *  The register used to read the FIRST page only (default 50) and stop, so on a site with a
 *  51st board that board was missing from the list, its search and every count built on it —
 *  with nothing on screen saying so. Exported for its test. */
export async function collectPages<T>(
  fetchPage: (token: string | undefined) => Promise<{ items: T[]; next?: string | undefined }>,
): Promise<T[]> {
  const out: T[] = [];
  let token: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const { items, next } = await fetchPage(token);
    out.push(...items);
    if (!next) return out;
    token = next;
  }
  throw new Error(`microcontroller list did not end after ${MAX_PAGES} pages`);
}

export function useMicrocontrollers(filter: McFilter = {}) {
  return useQuery({
    queryKey: [...mcKeys.list, filter],
    queryFn: () =>
      collectPages(async (token) => {
        const page = await api.get<Paged<'microcontrollers', Microcontroller>>(
          withQuery(mcEndpoints.list, {
            grow_room_id: filter.growRoomId,
            grow_room_rack_id: filter.growRoomRackId,
            plumbing_room_id: filter.plumbingRoomId,
            page_size: MC_PAGE_SIZE,
            page_token: token,
          }),
        );
        return { items: page.microcontrollers ?? [], next: page.next_page_token };
      }),
  });
}

export function useMicrocontroller(id: string) {
  return useQuery({
    queryKey: mcKeys.one(id),
    queryFn: () => api.get<Microcontroller>(mcEndpoints.one(id)),
    enabled: Boolean(id),
  });
}

export function useMcStatuses(filter: McStatusFilter = {}) {
  return useQuery({
    queryKey: [...mcKeys.statuses, filter],
    queryFn: async () =>
      (
        await api.get<Paged<'statuses', McStatusEntry>>(
          withQuery(mcEndpoints.statuses, { status: filter.status }),
        )
      ).statuses ?? [],
    refetchInterval: 15_000,
  });
}

export function useMcStatus(id: string) {
  return useQuery({
    queryKey: mcKeys.status(id),
    queryFn: () => api.get<McStatusEntry>(mcEndpoints.status(id)),
    enabled: Boolean(id),
    refetchInterval: 10_000,
  });
}

/** A parent's child controllers (empty for a leaf/childless device). */
export function useMcChildren(id: string, enabled = true) {
  return useQuery({
    queryKey: mcKeys.children(id),
    queryFn: async () => (await api.get<{ children: McChild[] }>(mcEndpoints.children(id))).children ?? [],
    enabled: Boolean(id) && enabled,
  });
}

export function useMcHistory(id: string) {
  return useQuery({
    queryKey: mcKeys.history(id),
    queryFn: async () =>
      (await api.get<Paged<'entries', McHistoryEntry>>(mcEndpoints.history(id))).entries ?? [],
    enabled: Boolean(id),
  });
}

/** The global install/move/removal audit log across every device (newest first),
 *  a page at a time. The backend pages by offset (`page_token` = the next offset,
 *  `""` once a page comes back short) at 100 rows by default — reading only the
 *  first page, as this used to, silently dropped every older record. */
export const MC_HISTORY_PAGE = 100;
function fetchMcHistoryPage(pageToken?: string) {
  return api.get<Paged<'entries', McHistoryEntry>>(
    withQuery(mcEndpoints.historyAll, {
      page_size: String(MC_HISTORY_PAGE),
      ...(pageToken ? { page_token: pageToken } : {}),
    }),
  );
}

export function useMcHistoryAll() {
  return useInfiniteQuery({
    queryKey: mcKeys.historyAll,
    queryFn: ({ pageParam }) => fetchMcHistoryPage(pageParam),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_page_token || undefined,
  });
}

/** A refresh somebody asked for: back to the NEWEST page, fresh. The log pages by OFFSET, so
 *  a row written since would shift every older row across a page boundary and a refetch of
 *  the pages already held would read the same row twice. The newest page is fetched on its
 *  own and REPLACES the pages in one write — the rows on screen stay until it lands, and the
 *  next-page token that comes with it is consistent with it. */
export function useRefreshMcHistory() {
  const qc = useQueryClient();
  return async () => {
    const fresh = await fetchMcHistoryPage();
    qc.setQueryData(mcKeys.historyAll, { pages: [fresh], pageParams: [''] });
  };
}

export function useCreateMc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateMcRequest) => api.post<Microcontroller>(mcEndpoints.list, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useUpdateMc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: UpdateMcRequest & { id: string }) =>
      api.patch<Microcontroller>(mcEndpoints.one(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

/** Retire a device end-of-life (auto-unassigns, sets lifecycle=retired). Devices are never deleted. */
export function useRetireMc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: RetireMcRequest & { id: string }) =>
      api.post<Microcontroller>(mcEndpoints.retire(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useAssignMcToGrowRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignGrowRoomRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignGrowRoom(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useAssignMcToGrowRoomRack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignGrowRoomRackRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignGrowRoomRack(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useAssignMcToGrowRoomRackLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignGrowRoomRackLevelRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignGrowRoomRackLevel(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

/**
 * R-232 — place a controller at a location, any depth, any room kind.
 *
 * Prefer this over the five typed hooks for new UI: it works for an office or
 * electrical room too, and the server still fills the typed slot when the target
 * happens to be a grow/plumbing entity, so nothing downstream changes.
 */
export function useAssignMcToLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignLocationRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignLocation(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useAssignMcToPlumbingRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignPlumbingRoomRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignPlumbingRoom(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useAssignMcToPlumbingNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: AssignPlumbingNodeRequest & { id: string }) =>
      api.post<void>(mcEndpoints.assignPlumbingNode(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}

export function useUnassignMc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(mcEndpoints.unassign(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: mcKeys.all }),
  });
}
